import React from 'react';
import { Card, CardBody } from '@heroui/react';
import { TFinancialMetrics } from '../dcfTypes';
import { useAppSelector } from '../../../core/rootHooks';
import { selectSignedInUserPublishedScenarioName } from '../../../user-management/appSelectors';
import { formatDateAsString } from '../../../utils/helpers';

type SummaryProps = {
	financialMetrics: TFinancialMetrics;
};

type SummaryItemProps = {
	label: string;
	value: string;
	prefix?: string;
	suffix?: string;
};

const SummaryItem: React.FC<SummaryItemProps> = ({ label, value, prefix = '', suffix = '' }) => {
	const isValueExists = value && value !== '-';

	return (
		<div className="space-y-4">
			<h3 className="font-semibold text-sm">{label}:</h3>
			<hr className="border-2 border-white" />
			<p className="text-lg">
				<span className="font-bold text-6xl">{isValueExists ? `${prefix}${value}` : '-'}</span>
				{isValueExists && <span className="text-sm"> {suffix}</span>}
			</p>
			<hr className="border border-dotted" />
		</div>
	);
};

function safeVal(v: number | null | undefined): string {
	if (v == null || !isFinite(v)) return '-';
	return v.toString();
}

const VesselSummary: React.FC<SummaryProps> = ({
	financialMetrics = {
		projectDcfValue: null,
		equityDcfValue: null,
		projectIrr: null,
		equityIrr: null,
		projectNpv: null,
		equityNpv: null,
		ltvAtFinance: null,
	},
}) => {
	const scenarioName: string = useAppSelector(selectSignedInUserPublishedScenarioName);
	const relatedDate = new Date().toString();
	const formattedDate = formatDateAsString(relatedDate, { year: 'numeric', month: 'long' });

	return (
		<Card className="bg-msi-orange-color text-white p-2 w-full h-full shadow-none border border-msi-darkgrey-color-300 rounded">
			<CardBody>
				<div className="grid grid-cols-2 gap-4 h-full">
					<SummaryItem label="DCF Value (Project)" value={safeVal(financialMetrics.projectDcfValue)} prefix="$" suffix="Mn" />
					<SummaryItem label="DCF Value (Equity)" value={safeVal(financialMetrics.equityDcfValue)} prefix="$" suffix="Mn" />
					<SummaryItem label="IRR (Project)" value={safeVal(financialMetrics.projectIrr)} suffix="%" />
					<SummaryItem label="IRR (Equity)" value={safeVal(financialMetrics.equityIrr)} suffix="%" />
				</div>
				<div className="mt-2 text-right">
					<h4 className="font-semibold">
						as of {formattedDate} (MSI Scenario {scenarioName})
					</h4>
				</div>
			</CardBody>
		</Card>
	);
};

export default VesselSummary;
